import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRedux } from 'hooks';
import { openWorkspaceTab } from 'redux/workspaceTabs/actions';
import { MAX_WORKSPACE_TABS, WorkspaceTab } from 'redux/workspaceTabs/types';
import { getWorkspaceTabKey, getWorkspaceTabTitle } from './tabTitle';

type WorkspaceTabsSyncProps = {
  onLimitReached: () => void;
};

/**
 * Keeps the workspace tab list in sync with the current private-route location.
 * One tab per unique pathname (open again focuses existing tab).
 * New tabs are blocked once MAX_WORKSPACE_TABS is reached.
 */
const WorkspaceTabsSync = ({ onLimitReached }: WorkspaceTabsSyncProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch, appSelector } = useRedux();
  const { tabs, activeKey } = appSelector((state) => ({
    tabs: state.WorkspaceTabs.tabs as WorkspaceTab[],
    activeKey: state.WorkspaceTabs.activeKey as string | null,
  }));
  const tabsRef = useRef(tabs);
  const activeKeyRef = useRef(activeKey);
  tabsRef.current = tabs;
  activeKeyRef.current = activeKey;

  useEffect(() => {
    const { pathname, search } = location;
    if (!pathname || pathname.startsWith('/account')) {
      return;
    }

    const key = getWorkspaceTabKey(pathname, search);
    const currentTabs = tabsRef.current;
    const existing = currentTabs.find((tab) => tab.key === key);

    if (!existing && currentTabs.length >= MAX_WORKSPACE_TABS) {
      onLimitReached();
      const active =
        currentTabs.find((tab) => tab.key === activeKeyRef.current) || currentTabs[currentTabs.length - 1];
      if (active) {
        navigate(`${active.pathname}${active.search || ''}`, { replace: true });
      }
      return;
    }

    dispatch(
      openWorkspaceTab({
        key,
        pathname,
        search,
        title: getWorkspaceTabTitle(pathname),
      })
    );
  }, [dispatch, location.pathname, location.search, navigate, onLimitReached]);

  return null;
};

export default WorkspaceTabsSync;
